// Notification rules, monitored alerts, notification subscriptions API
import { api } from './client';

// ============================================================================
// NOTIFICATION RULES (EXPOSURE POLICIES) API
// ============================================================================

export const notificationRulesAPI = {
  // Get notification rules for a client
  getRules: (clientId: number) => {
    return api.get(`/clients/${clientId}/notification-rules`);
  },

  // Create default notification rules for a client
  // type: 'all' (default), 'sme' (standard scan monitoring), 'recon' (recon/OSINT monitoring)
  createDefaultRules: (clientId: number, type: 'all' | 'sme' | 'recon' = 'all') => {
    return api.post(`/clients/${clientId}/default-notification-rules?type=${type}`);
  },

  // Get available event types for notification rules
  getEventTypes: () => {
    return api.get('/notification-rules/event-types');
  },

  // Update a notification rule (toggle active, change settings)
  updateRule: (ruleId: number, updates: {
    name?: string;
    description?: string;
    event_types?: string;
    event_categories?: string;
    min_severity?: string;
    notify_immediately?: boolean;
    include_in_digest?: boolean;
    digest_frequency?: string;
    notify_via_email?: boolean;
    is_active?: boolean;
    only_internet_exposed?: boolean;
    only_production?: boolean;
  }) => {
    return api.put(`/notification-rules/${ruleId}`, updates);
  },

  // Delete a notification rule
  deleteRule: (ruleId: number) => {
    return api.delete(`/notification-rules/${ruleId}`);
  },
};

// ============================================================================
// MONITORED ALERTS API (Proactive SSL Certificate Expiry Monitoring)
// ============================================================================

export const monitoredAlertsAPI = {
  // Get monitored alerts for a project
  getByProject: (projectId: number, params?: {
    type?: string;
    status?: 'active' | 'paused' | 'all';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.append('type', params.type);
    if (params?.status) searchParams.append('status', params.status);
    const queryString = searchParams.toString();
    return api.get(`/projects/${projectId}/monitored-alerts${queryString ? '?' + queryString : ''}`);
  },

  // Get all monitored alerts (admin view)
  getAll: (params?: {
    type?: string;
    status?: 'active' | 'paused' | 'inactive' | 'all';
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.append('type', params.type);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString();
    return api.get(`/monitored-alerts${queryString ? '?' + queryString : ''}`);
  },

  // Get a single monitored alert
  get: (alertId: number) => {
    return api.get(`/monitored-alerts/${alertId}`);
  },

  // Update a monitored alert (change thresholds, pause/resume, etc.)
  update: (alertId: number, updates: {
    alert_30_days?: boolean;
    alert_14_days?: boolean;
    alert_7_days?: boolean;
    alert_1_day?: boolean;
    alert_expired?: boolean;
    is_active?: boolean;
    is_paused?: boolean;
    pause_reason?: string;
  }) => {
    return api.put(`/monitored-alerts/${alertId}`, updates);
  },

  // Delete a monitored alert
  delete: (alertId: number) => {
    return api.delete(`/monitored-alerts/${alertId}`);
  },

  // Manually verify a monitored alert (re-check certificate)
  verify: (alertId: number) => {
    return api.post(`/monitored-alerts/${alertId}/verify`);
  },

  // Pause a monitored alert
  pause: (alertId: number, reason?: string) => {
    return api.post(`/monitored-alerts/${alertId}/pause`, { reason });
  },

  // Resume a paused monitored alert
  resume: (alertId: number) => {
    return api.post(`/monitored-alerts/${alertId}/resume`);
  },

  // Get proactive alert system status
  getStatus: () => {
    return api.get('/proactive-alerts/status');
  },

  // Backfill monitored alerts for a specific project from existing SSL scan results
  backfillProject: (projectId: number) => {
    return api.post<{
      message: string;
      created: number;
      updated: number;
      skipped: number;
      errors: number;
      total_processed: number;
    }>(`/projects/${projectId}/monitored-alerts/backfill`);
  },
};

// ============================================================================
// NOTIFICATION SUBSCRIPTIONS API
// ============================================================================

export interface NotificationEventType {
  id: string;
  name: string;
  description: string;
  category: string;
  default_enabled: boolean;
}

export interface NotificationSubscription {
  id: number;
  client_contact_id: number;
  client_id: number;
  project_id?: number;
  event_type: string;
  event_types?: string[];
  min_severity?: string;
  only_kev: boolean;
  only_internet_facing: boolean;
  delivery_method: string;
  webhook_url?: string;
  is_digest: boolean;
  digest_frequency?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contact?: {
    id: number;
    name: string;
    email: string;
  };
  client?: {
    id: number;
    name: string;
  };
}

export interface NotificationPreferences {
  email_enabled: boolean;
  webhook_enabled: boolean;
  in_app_enabled: boolean;
  subscriptions: NotificationSubscription[];
}

export interface NotificationHistoryItem {
  id: number;
  event_type: string;
  channel: string;
  status: 'sent' | 'failed' | 'pending';
  message: string;
  created_at: string;
}

export const notificationSubscriptionsAPI = {
  // Get available event types
  getEventTypes: () => {
    console.log('📋 Fetching notification event types');
    return api.get<NotificationEventType[]>('/notification-subscriptions/event-types');
  },

  // Get all subscriptions (admin)
  getSubscriptions: () => {
    console.log('🔔 Fetching notification subscriptions');
    return api.get<NotificationSubscription[]>('/notification-subscriptions');
  },

  // Create a new subscription (supports multiple event_types)
  createSubscription: (data: {
    client_id: number;
    client_contact_id: number;
    event_types: string[];
    delivery_method: string;
    webhook_url?: string;
    min_severity?: string;
    only_kev: boolean;
    only_internet_facing: boolean;
    is_digest: boolean;
    digest_frequency?: string;
  }) => {
    console.log('➕ Creating notification subscription');
    return api.post<NotificationSubscription | NotificationSubscription[]>('/notification-subscriptions', data);
  },

  // Update a subscription
  updateSubscription: (id: number, data: Partial<NotificationSubscription>) => {
    console.log('✏️ Updating notification subscription:', id);
    return api.put<NotificationSubscription>(`/notification-subscriptions/${id}`, data);
  },

  // Delete a subscription
  deleteSubscription: (id: number) => {
    console.log('🗑️ Deleting notification subscription:', id);
    return api.delete(`/notification-subscriptions/${id}`);
  },

  // Bulk update subscriptions
  bulkUpdate: (subscriptions: Array<{ id?: number; event_type: string; channel: string; enabled: boolean }>) => {
    console.log('📦 Bulk updating notification subscriptions');
    return api.post('/notification-subscriptions/bulk', { subscriptions });
  },

  // Get current user's preferences
  getMyPreferences: () => {
    console.log('⚙️ Fetching my notification preferences');
    return api.get<NotificationPreferences>('/notification-subscriptions/my-preferences');
  },

  // Get notification history
  getHistory: (params?: { limit?: number; offset?: number; event_type?: string }) => {
    console.log('📜 Fetching notification history');
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    if (params?.event_type) searchParams.append('event_type', params.event_type);
    const query = searchParams.toString();
    return api.get<NotificationHistoryItem[]>(`/notification-subscriptions/history${query ? `?${query}` : ''}`);
  },
};

// ============================================================================
// MONITORED ALERTS ADMIN API
// ============================================================================

export const monitoredAlertsAdminAPI = {
  // Backfill monitored alerts from existing SSL certificate scan results
  backfillFromSSL: () => {
    return api.post<{
      message: string;
      created: number;
      updated: number;
      skipped: number;
      errors: number;
      total_processed: number;
    }>('/admin/monitored-alerts/backfill');
  },
};
